/**
 * @file useAnchoredTip.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-01
 * @modified 2026-10-01
 * @description A small information panel anchored to a trigger and drawn at a
 *              fixed position (in a portal, by the caller), so no scrolling
 *              table, overflow:auto box or sticky cell can clip or cover it.
 *
 *              One panel per hook, however many triggers: a grid of forty rows
 *              shares a single tip, keyed by the id of the row it belongs to.
 *
 *              How it opens and closes:
 *                - pointer over the trigger (mouse/pen only) opens it, and
 *                  pointer out closes it;
 *                - keyboard focus opens it, and blur closes it;
 *                - a click or tap pins it open; a second one closes it (a
 *                  keyboard Enter/Space toggles it directly);
 *                - toggle(id, mode) opens it from somewhere else, such as a
 *                  shortcut key in another control, and closeIfMode(mode) lets
 *                  that control close it again on its own blur;
 *                - Escape, a press anywhere off the trigger, any scroll and a
 *                  window resize close it, whatever opened it. Closing on
 *                  scroll rather than following it: a fixed panel left behind
 *                  by a scrolled row would point at the wrong material.
 *
 *              Placement: below the trigger when it fits, otherwise above, and
 *              kept on screen at both side edges. Measured in a layout effect,
 *              so the first frame is already in the right place.
 *
 *              `anchorRect(trigger)` (optional, keep it stable — define it at
 *              module scope) says what box to place against, when that is more
 *              than the trigger: a small icon on the first line of a two-line
 *              label would otherwise open its panel over the second line.
 *
 *              Usage:
 *                const tip = useAnchoredTip();
 *                <button ref={tip.anchorRef(id)} {...tip.triggerProps(id)} />
 *                {tip.openId && createPortal(
 *                  <div ref={tip.panelRef} id={tip.panelId} role="tooltip"
 *                       style={tip.panelStyle} data-placement={tip.placement}>…</div>,
 *                  document.body)}
 *
 *              SearchHelp has an older, simpler copy of the positioning. It
 *              could move onto this hook; it is on every search screen, so that
 *              is its own change.
 */

import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';

const triggerRect = (el) => el.getBoundingClientRect();

export default function useAnchoredTip({ gap = 6, margin = 8, anchorRect = triggerRect } = {}) {
  // { id, mode } — mode is 'hover' | 'focus' | 'pinned' | anything a caller
  // passes to toggle(). Null when closed.
  const [tip, setTip] = useState(null);
  const [pos, setPos] = useState(null);
  const anchors = useRef(new Map());
  const panelRef = useRef(null);
  // useId's punctuation has changed between React versions; an id is safer as
  // plain characters wherever it ends up (aria-describedby, a CSS selector).
  const panelId = `anchored-tip-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const openId = tip ? tip.id : null;

  const close = useCallback(() => setTip(null), []);

  const closeIfMode = useCallback((mode) => {
    setTip((prev) => (prev && prev.mode === mode ? null : prev));
  }, []);

  // Already open for this id: keep whatever opened it. Hover arriving on a
  // pinned tip must not demote it to one that closes on the way out.
  const open = useCallback((id, mode) => {
    if (!anchors.current.has(id)) return;
    setTip((prev) => (prev && prev.id === id ? prev : { id, mode }));
  }, []);

  /** Open for `id` from elsewhere, or close if it is already open for `id`. */
  const toggle = useCallback((id, mode = 'pinned') => {
    if (!anchors.current.has(id)) return;
    setTip((prev) => (prev && prev.id === id ? null : { id, mode }));
  }, []);

  // One stable callback per id, so React calls it with null only when the
  // trigger really unmounts — not on every render, as a fresh arrow would be.
  const refFns = useRef(new Map());
  const anchorRef = useCallback((id) => {
    let fn = refFns.current.get(id);
    if (!fn) {
      fn = (el) => {
        if (el) { anchors.current.set(id, el); return; }
        anchors.current.delete(id);
        // The trigger went away under an open tip (its row left the list):
        // close rather than leave a panel describing something not on screen.
        setTip((prev) => (prev && prev.id === id ? null : prev));
      };
      refFns.current.set(id, fn);
    }
    return fn;
  }, []);

  const triggerProps = (id) => ({
    onPointerEnter: (e) => { if (e.pointerType !== 'touch') open(id, 'hover'); },
    onPointerLeave: (e) => {
      if (e.pointerType === 'touch') return;
      setTip((prev) => (prev && prev.id === id && prev.mode === 'hover' ? null : prev));
    },
    onFocus: () => open(id, 'focus'),
    onBlur: () => {
      setTip((prev) => (prev && prev.id === id && prev.mode !== 'hover' ? null : prev));
    },
    onClick: (e) => {
      // detail is 0 for a click synthesised from Enter or Space. Focus has
      // already opened the tip by then, so the key closes it — otherwise the
      // first press would "pin" a tip that is already showing and do nothing
      // visible. A pointer click opens on hover first, so it pins instead.
      const fromKeyboard = e.detail === 0;
      setTip((prev) => {
        if (prev && prev.id === id) {
          if (prev.mode === 'pinned' || (fromKeyboard && prev.mode === 'focus')) return null;
        }
        return { id, mode: 'pinned' };
      });
    },
    'aria-expanded': openId === id,
    'aria-describedby': openId === id ? panelId : undefined
  });

  useLayoutEffect(() => {
    if (openId === null) { setPos(null); return undefined; }
    const panel = panelRef.current;
    const place = () => {
      const anchor = anchors.current.get(openId);
      if (!anchor || !panel) return;
      const a = anchorRect(anchor);
      // Layout size, not getBoundingClientRect: the panel may be mid-way
      // through an opening scale animation, and a rect measured then is
      // smaller than the panel will be — a panel placed above its trigger
      // would come to rest over it.
      const p = { width: panel.offsetWidth, height: panel.offsetHeight };
      const vw = document.documentElement.clientWidth || window.innerWidth;
      const vh = window.innerHeight;
      const roomBelow = vh - a.bottom - gap - margin;
      const roomAbove = a.top - gap - margin;
      const below = p.height <= roomBelow || roomBelow >= roomAbove;
      const top = below ? a.bottom + gap : a.top - gap - p.height;
      const next = {
        top: Math.max(margin, Math.min(top, vh - p.height - margin)),
        left: Math.max(margin, Math.min(a.left - 4, vw - p.width - margin)),
        placement: below ? 'below' : 'above'
      };
      setPos((prev) => (prev && prev.top === next.top && prev.left === next.left
        && prev.placement === next.placement ? prev : next));
    };
    place();
    // The content can change while it is open (a figure arriving, a line
    // appearing), and a panel placed ABOVE its trigger would then grow down
    // over it. Re-place on any change of size.
    if (!panel || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(place);
    observer.observe(panel);
    return () => observer.disconnect();
  }, [openId, gap, margin, anchorRect]);

  useEffect(() => {
    if (openId === null) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setTip(null); };
    const onPointerDown = (e) => {
      const anchor = anchors.current.get(openId);
      if (anchor && anchor.contains(e.target)) return;
      setTip(null);
    };
    const onScrollOrResize = () => setTip(null);
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointerDown, true);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointerDown, true);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [openId]);

  return {
    openId,
    mode: tip ? tip.mode : null,
    panelId,
    panelRef,
    // Hidden until measured, so it never shows a frame at the corner of the page.
    panelStyle: pos ? { top: pos.top, left: pos.left } : { top: 0, left: 0, visibility: 'hidden' },
    placement: pos ? pos.placement : 'below',
    anchorRef,
    triggerProps,
    toggle,
    close,
    closeIfMode
  };
}
